import { FileText, Home, Info, Linkedin, Mail, MapPin, MessageSquare, Phone, Star } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import './FooterStyles.css'; // Import custom styles for footer
import { VisitorLocation } from './VisitorLocation';

// Define the City type locally
interface City {
  id: string;
  name: string;
  slug: string;
  description: string;
  imageUrl: string;
}

// Embed JSON string directly (shortened for brevity, ensure full data is here)
const CITIES_JSON_STRING = `[
  {
    "id": "1",
    "name": "Campinas",
    "slug": "campinas",
    "description": "Campinas é um município brasileiro...",
    "imageUrl": "/assets/images/cities/periciatecnica-pericia-engenharia-campinas-vista-aerea.jpg"
  },
  {
    "id": "2",
    "name": "Jundiaí",
    "slug": "jundiai",
    "description": "Jundiaí é um município brasileiro...",
    "imageUrl": "/assets/images/cities/periciatecnica-pericia-engenharia-jundiai-serra-japi.jpg"
  },
  {
    "id": "3",
    "name": "Piracicaba",
    "slug": "piracicaba",
    "description": "Piracicaba é um município...",
    "imageUrl": "/assets/images/cities/periciatecnica-pericia-engenharia-piracicaba-rio-ponte.jpg"
  },
  {
    "id": "4",
    "name": "Limeira",
    "slug": "limeira",
    "description": "Limeira é um município brasileiro...",
    "imageUrl": "/assets/images/cities/periciatecnica-pericia-engenharia-limeira-centro.jpg"
  },
  {
    "id": "5",
    "name": "Indaiatuba",
    "slug": "indaiatuba",
    "description": "Indaiatuba é um município...",
    "imageUrl": "/assets/images/cities/periciatecnica-pericia-engenharia-indaiatuba-parque-ecologico.jpg"
  },
  {
    "id": "6",
    "name": "Paulínia",
    "slug": "paulinia",
    "description": "Paulínia é um município brasileiro...",
    "imageUrl": "/assets/images/cities/periciatecnica-pericia-engenharia-paulinia-replan.jpg"
  }
]`;

// Parse the JSON string
let CITIES: City[] = [];
try {
  CITIES = JSON.parse(CITIES_JSON_STRING);
} catch (error) {
  console.error("Failed to parse embedded CITIES JSON:", error);
}

export default function Footer() {
  const businessLocation = { lat: -22.901244, lng: -47.030783 };
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    // Check if the device is mobile using userAgent
    const checkMobile = () => {
      const userAgent = navigator.userAgent.toLowerCase();
      const isMobileDevice = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent);
      setIsMobile(isMobileDevice);
    };

    // Initial check
    checkMobile();

    // Set up event listener for window resize to recheck
    window.addEventListener('resize', checkMobile);

    // Clean up
    return () => {
      window.removeEventListener('resize', checkMobile);
    };
  }, []);

  // Function to open Google Maps with the business location
  const openGoogleMaps = () => {
    const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${businessLocation.lat},${businessLocation.lng}`;
    window.open(mapsUrl, '_blank');
  };

  // Function to handle phone click based on device type
  const handlePhoneClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (!isMobile) {
      e.preventDefault();
      // Show phone number in a nice way for desktop users
      const phoneTooltip = document.createElement('div');
      phoneTooltip.className = 'fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white dark:bg-gray-800 p-4 rounded-lg shadow-lg z-50 flex flex-col items-center';
      phoneTooltip.innerHTML = `
        <div class="flex items-center mb-2">
          <Phone class="h-5 w-5 text-blue-600 mr-2" />
          <span class="text-lg font-semibold text-gray-900 dark:text-white">Telefone de Contato</span>
        </div>
        <a href="tel:+5519991133163" class="text-blue-600 dark:text-blue-500 text-xl font-bold">(19) 99113-3163</a>
        <p class="text-gray-600 dark:text-gray-400 text-sm mt-2">Clique no número para copiar</p>
        <button id="phone-tooltip-close" class="mt-4 px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-white rounded hover:bg-gray-300 dark:hover:bg-gray-600">Fechar</button>
      `;
      document.body.appendChild(phoneTooltip);
      
      // Add event listener to close button
      const closeButton = document.getElementById('phone-tooltip-close');
      if (closeButton) {
        closeButton.addEventListener('click', () => {
          document.body.removeChild(phoneTooltip);
        });
      }
      
      // Auto close after 5 seconds
      setTimeout(() => {
        if (document.body.contains(phoneTooltip)) {
          document.body.removeChild(phoneTooltip);
        }
      }, 5000);
    }
    // On mobile, the default behavior (tel: link) will work
  };

  return (
    <footer className="bg-gray-50 dark:bg-gray-900 py-6 border-t border-gray-200 dark:border-gray-800">
      <div className="container mx-auto px-4 max-w-7xl">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {/* Column 1: Company Info */}
          <div>
            <div className="mb-3">
              <h2 className="text-xl font-semibold text-blue-600 dark:text-blue-500">Moacyr Moura Ferreira</h2>
              <p className="text-gray-700 dark:text-gray-400 mt-1 text-sm">
                Engenharia, Perícias e Avaliações
              </p>
            </div>
            
            {/* Social Icons */}
            <div className="flex flex-wrap gap-4 mb-4">
              <a
                href="https://www.linkedin.com/in/moacyr-moura-ferreira-542565b/"
                target="_blank"
                rel="noopener noreferrer"
                className="social-icon"
                aria-label="LinkedIn"
              >
                <Linkedin className="h-5 w-5" />
              </a>
              
              <a
                href="mailto:moacyr.mmf@gmail.com"
                className="social-icon"
                aria-label="Email"
              >
                <Mail className="h-5 w-5" />
              </a>
              
              <button
                onClick={openGoogleMaps}
                className="social-icon"
                aria-label="Localização no Google Maps"
              >
                <MapPin className="h-5 w-5" />
              </button>
              
              <a
                href="https://wa.me/5519991133163?text=Ola%20gostaria%20de%20maiores%20informacoes%2C%20eu%20vim%20do%20seu%20site%20periciatecnic"
                target="_blank"
                rel="noopener noreferrer"
                className="social-icon"
                aria-label="WhatsApp"
              >
                <svg 
                  xmlns="http://www.w3.org/2000/svg" 
                  viewBox="0 0 24 24" 
                  fill="currentColor" 
                  className="h-5 w-5"
                >
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                </svg>
              </a>
              
              <a
                href="tel:+5519991133163"
                className="social-icon"
                aria-label="Telefone"
                onClick={handlePhoneClick}
              >
                <Phone className="h-5 w-5" />
              </a>
            </div>
          </div>
          
          {/* Column 2: Quick Links */}
          <div>
            <h3 className="font-bold text-base mb-3 text-gray-900 dark:text-white">Links Rápidos</h3>
            <ul className="space-y-1.5 text-sm">
              <li>
                <a href="/" className="text-gray-700 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
                  <Home className="h-3.5 w-3.5 mr-1.5 inline-block" />
                  Home
                </a>
              </li>
              <li>
                <a href="/#services" className="text-gray-700 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
                  <Star className="h-3.5 w-3.5 mr-1.5 inline-block" />
                  Serviços
                </a>
              </li>
              <li>
                <a href="/#about" className="text-gray-700 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
                  <Info className="h-3.5 w-3.5 mr-1.5 inline-block" />
                  Sobre
                </a>
              </li>
              <li>
                <a href="/#contact" className="text-gray-700 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
                  <MessageSquare className="h-3.5 w-3.5 mr-1.5 inline-block" />
                  Contato
                </a>
              </li>
              <li>
                <a href="/blog" className="text-gray-700 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
                  <FileText className="h-3.5 w-3.5 mr-1.5 inline-block" />
                  Blog
                </a>
              </li>
              <li>
                <a href="/#testimonials" className="text-gray-700 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
                  <Star className="h-3.5 w-3.5 mr-1.5 inline-block" />
                  Referências
                </a>
              </li>
            </ul>
          </div>
          
          {/* Column 3: Services */}
          <div>
            <h3 className="font-bold text-base mb-3 text-gray-900 dark:text-white">Serviços</h3>
            <ul className="space-y-1.5 text-sm">
              <li>
                <a href="/#services" className="text-gray-700 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
                  Perícias Judiciais
                </a>
              </li>
              <li>
                <a href="/#services" className="text-gray-700 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
                  Avaliações de Imóveis
                </a>
              </li>
              <li>
                <a href="/#services" className="text-gray-700 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
                  Inspeções Prediais
                </a>
              </li>
              <li>
                <a href="/#services" className="text-gray-700 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
                  Consultoria Técnica
                </a>
              </li>
            </ul>
          </div>
          
          {/* Column 4: Service Areas (NEW) */}
          <div>
            <h3 className="font-bold text-base mb-3 text-gray-900 dark:text-white">Áreas de Atuação</h3>
            <ul className="space-y-1.5 text-sm">
              {CITIES.map((city) => (
                <li key={city.id}>
                  <a 
                    href={`/perito-judicial-em-${city.slug}`}
                    className="text-gray-700 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                    aria-label={`Serviços de perícia judicial em ${city.name}`}
                  >
                    <MapPin className="h-3.5 w-3.5 mr-1.5 inline-block" />
                    {city.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>
        
        <div className="mt-6">
          <VisitorLocation />
        </div>
        
        <div className="border-t border-gray-200 dark:border-gray-800 mt-4 pt-4 text-center">
          <p className="text-gray-700 dark:text-gray-400 text-sm">
            © {new Date().getFullYear()} Perícia Técnica. Todos os direitos reservados.
          </p>
        </div>
      </div>
    </footer>
  );
}
